import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, useTheme } from "@/App";
import {
  Link2,
  LayoutDashboard,
  LinkIcon,
  BarChart3,
  Layout,
  QrCode,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
  ChevronRight,
  ChevronLeft,
  Crown,
} from "lucide-react";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: BarChart3, label: "Analytics", path: "/analytics" },
  { icon: LinkIcon, label: "Links", path: "/links" },
  { icon: Layout, label: "Microsites", path: "/microsites" },
  { icon: QrCode, label: "QR Code", path: "/qrcode" },
  { icon: Crown, label: "Subscription", path: "/subscription" },
  { icon: Settings, label: "Settings", path: "/settings" },
];

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("dashboard_sidebar_collapsed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("dashboard_sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* noop */ }
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // iconOnly cuma berlaku untuk sidebar desktop yang di-collapse — sheet
  // mobile selalu tampil penuh dengan teks. Style ditulis inline (bukan
  // lewat class .sidebar-link) supaya padding icon-only tidak kalah cascade
  // sama aturan px-4 bawaan .sidebar-link di App.css.
  const NavContent = ({ onItemClick, iconOnly = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`border-b border-border ${iconOnly ? "px-4 py-6" : "p-6"}`}>
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <Link2 className="w-5 h-5 text-primary-foreground" />
          </div>
          {!iconOnly && (
            <span className="text-lg font-bold truncate" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Campus Link
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              title={iconOnly ? item.label : undefined}
              className={`flex items-center gap-3 rounded-lg transition-all duration-200 ${
                iconOnly ? "justify-center px-0 py-3" : "px-4 py-3"
              } ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent"}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {!iconOnly && <span className="truncate">{item.label}</span>}
              {!iconOnly && isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0" />}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="p-3 border-t border-border">
        <Button
          variant="ghost"
          title={iconOnly ? "Logout" : undefined}
          className={`w-full ${iconOnly ? "justify-center px-0" : "justify-start gap-3"}`}
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!iconOnly && <span>Logout</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 bg-card border-r border-border hidden lg:flex flex-col transition-[width] duration-200 ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        <NavContent iconOnly={collapsed} />
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="absolute top-7 -right-3 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors shadow-sm"
          title={collapsed ? "Perluas menu" : "Ciutkan menu"}
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card/95 backdrop-blur-md border-b border-border z-50">
        <div className="flex items-center justify-between h-full px-4">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <NavContent onItemClick={() => setSidebarOpen(false)} />
            </SheetContent>
          </Sheet>

          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Link2 className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Campus Link
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header
        className={`hidden lg:flex fixed top-0 right-0 h-16 bg-card/95 backdrop-blur-md border-b border-border z-40 items-center justify-between px-8 transition-[left] duration-200 ${
          collapsed ? "left-20" : "left-64"
        }`}
      >
        <div></div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-desktop">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className={`pt-16 min-h-screen transition-[margin] duration-200 ${collapsed ? "lg:ml-20" : "lg:ml-64"}`}>
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
